<template>
  <div class="payment-breakdown-chart">
    <h3>Payment Methods</h3>

    <div class="chart-container">
      <canvas ref="chartCanvas"></canvas>
    </div>

    <div class="legend">
      <div
        v-for="(payment, index) in data"
        :key="payment.method"
        class="legend-item"
        @mouseenter="highlightSegment(index)"
        @mouseleave="unhighlightSegment()"
      >
        <div class="legend-color" :style="{ background: colors[index % colors.length] }"></div>
        <div class="legend-details">
          <div class="legend-label">{{ payment.method }}</div>
          <div class="legend-value">
            <span class="amount">KES {{ formatNumber(payment.amount) }}</span>
            <span class="percentage">{{ payment.percent }}%</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default {
  name: 'PaymentBreakdownChart',

  props: {
    data: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      chart: null,
      colors: [
        '#3b82f6',
        '#10b981',
        '#f59e0b',
        '#ef4444',
        '#8b5cf6',
        '#ec4899',
        '#14b8a6',
        '#f97316'
      ]
    };
  },

  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true
    }
  },

  mounted() {
    this.initChart();
  },

  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  },

  methods: {
    initChart() {
      const ctx = this.$refs.chartCanvas.getContext('2d');

      this.chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: [],
          datasets: [{
            data: [],
            backgroundColor: this.colors,
            borderWidth: 2,
            borderColor: '#fff',
            hoverBorderWidth: 3,
            hoverBorderColor: '#fff'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '70%',
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              titleFont: {
                size: 14,
                weight: '600'
              },
              bodyFont: {
                size: 13
              },
              padding: 12,
              cornerRadius: 8,
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = this.formatNumber(context.parsed);
                  const percent = this.data[context.dataIndex]?.percent || 0;
                  return `${label}: KES ${value} (${percent}%)`;
                }
              }
            }
          }
        }
      });

      this.updateChart();
    },

    updateChart() {
      if (!this.chart || !this.data || this.data.length === 0) return;

      this.chart.data.labels = this.data.map(item => item.method);
      this.chart.data.datasets[0].data = this.data.map(item => item.amount);

      this.chart.update();
    },

    highlightSegment(index) {
      if (!this.chart) return;

      this.chart.setActiveElements([{
        datasetIndex: 0,
        index: index
      }]);
      this.chart.update();
    },

    unhighlightSegment() {
      if (!this.chart) return;

      this.chart.setActiveElements([]);
      this.chart.update();
    },

    formatNumber(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>

<style scoped>
.payment-breakdown-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.payment-breakdown-chart h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
}

.chart-container {
  position: relative;
  height: 250px;
  margin-bottom: 20px;
}

.legend {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.legend-item:hover {
  background: #f8fafc;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-details {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.legend-label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.legend-value {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.amount {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.percentage {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  background: #f1f5f9;
  border-radius: 4px;
  color: #64748b;
}
</style>
